import React, { useState } from "react";
//#region types
import { FormFieldProps, SearchResultsFieldProps } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import InputField from "../baseFields/inputField";
import StyledButton from "../../buttons/styledButton";
import SubTitleWithIcon from "../../titles/subTitleWithIcon";
import { TableAsync } from "components/common/tables";
import { Grid, Typography } from "@mui/material";
import { useFormikContext } from "formik";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

function SearchResultsFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, handleOnBlurCustom, handleOnChangeCustom } = props;
  const extraProps = field.extraProps as SearchResultsFieldProps<T>;

  const formikContext = useFormikContext<T>();

  const [results, setSearchResults] = useState<any[] | undefined>(undefined);

  React.useEffect(() => {
    formikContext.initialValues[name] && onSearchClick();
  }, [formikContext.initialValues]);

  const onSearchClick = () => {
    extraProps
      .searchFunc(value)
      .then((res) => {
        setSearchResults(res ?? []);
      })
      .catch(() => setSearchResults([]));
  };

  const onResultItemClick = (row: any) => {
    extraProps.onItemSelect && extraProps.onItemSelect(row, formikContext);
  };

  return (
    <Grid container direction="column" className={cssExports.searchResultField}>
      <Grid container item className={cssExports.searchResultFieldInput}>
        <Grid item xs>
          <InputField
            key={`input-${name}`}
            label={LocalizationHelperInstance.getFormattedMessage(field.label)}
            inputBaseProps={{
              value: value ? value : "",
              name: name,
              type: "text",
              onBlur: handleOnBlurCustom.bind(null, name),
              disabled: props.disabled,
              readOnly: props.readonly,
            }}
            icon={field.icon}
            disabled={props.disabled}
            maxLength={field.maxLength}
            onChangeCustom={(value) => {
              handleOnChangeCustom(name, value);
            }}
          />
        </Grid>
        <Grid item className={cssExports.inputButton}>
          <StyledButton onClick={onSearchClick} disabled={!value}>
            {LocalizationHelperInstance.getFormattedMessage(
              extraProps.searchButtonText ?? TranslationKeys.Common.Search
            )}
          </StyledButton>
        </Grid>
      </Grid>
      {results !== undefined ? (
        results.length > 0 ? (
          <>
            {extraProps.subText && (
              <Grid container>
                <SubTitleWithIcon
                  subTitle={LocalizationHelperInstance.getFormattedMessage(extraProps.subText, extraProps.subText)}
                  icon={IconDefinitionConstants.WBolt}
                ></SubTitleWithIcon>
              </Grid>
            )}
            <Grid container>
              <TableAsync
                properties={{
                  ...extraProps.resultTableProperties,
                  select: {
                    description: "",
                    formatter: (row: any) => (
                      <StyledButton size="xs" onClick={onResultItemClick.bind(null, row)}>
                        {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Select)}
                      </StyledButton>
                    ),
                  },
                }}
                filter={results}
                maxRowCount={10}
                hidePaginationIfSinglePage
                alternateColors
              />
            </Grid>
          </>
        ) : (
          <Grid container>
            <Typography className={cssExports.recentItemsText}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.NotFound)}
            </Typography>
          </Grid>
        )
      ) : (
        <></>
      )}
    </Grid>
  );
}

export default SearchResultsFormField;
